/**
 * @file Classes of the model edges and edge arrays.
 */

import { MMNode } from './node-model';

export enum EdgeType {
  Consumption = 'Consumption',
  Tagged = 'Tagged',
  Bidirectional = 'bidirectional-arrow',
  Effect = 'Effect',
  Instrument = 'Instrument',
  Agent = 'Agent',
  Aggregation = 'Aggregation-participation',
  Exhibition = 'Exhibition-characterization',
  Generalization = 'Generalization-specialization',
  Classification = 'Classification-instantiation',
  Generation = 'Generation',
  Invocation = 'Invocation',
}

export const hierarchicalStructuralEdges = [
  EdgeType.Aggregation,
  EdgeType.Exhibition,
  EdgeType.Generalization,
  EdgeType.Classification,
];

export enum LinkQualifier {
  None = 'none',
  Condition = 'condition',
  Event = 'event',
}

export enum LinkNegation {
  None = 'none',
  Not = 'not',
}

class MMEdge {
  id: string;
  source: MMNode;
  target: MMNode;
  type: EdgeType;
  label: string;
  // 关系扩展字段
  tags: string[];
  description?: string;
  version?: string;
  updatedAt?: string;
  updatedBy?: string[];
  instanceFileName?: string;
  instanceFileUrl?: string;
  // 临时编辑标记（仅前端使用，不参与导出）
  __opmcvDirty?: boolean;
  __opmcvEditSnapshot?: {
    label: string;
    tags: string[];
    description: string;
    instanceFileName: string;
    instanceFileUrl: string;
  };
  linkQualifier: LinkQualifier;
  linkNegation: LinkNegation;
  originalEdges: Array<MMEdge>;
  derivedEdges: Array<MMEdge>;
  deleted: boolean;
  propagation: boolean;
  preferedEdge: MMEdge | null;

  constructor(
    id: string,
    source: MMNode,
    target: MMNode,
    type: EdgeType,
    propagation: boolean,
    originalEdge: MMEdge | undefined = undefined,
    label: string = '',
    linkQualifier: LinkQualifier = LinkQualifier.None,
    linkNegation: LinkNegation = LinkNegation.None
  ) {
    this.id = id;
    this.source = source;
    this.target = target;
    this.type = type;
    this.label = label;
    this.tags = [];
    this.description = '';
    this.version = '1.0';
    this.updatedAt = '';
    this.updatedBy = [];
    this.instanceFileName = '';
    this.instanceFileUrl = '';
    this.linkQualifier = linkQualifier;
    this.linkNegation = linkNegation;
    this.originalEdges = originalEdge !== undefined ? [originalEdge] : [];
    this.derivedEdges = [];
    this.deleted = false;
    this.propagation = propagation;
    this.preferedEdge = null;
  }

  /**
   * Add a derived edge the .derivedEdge array.
   * @param derivedEdge
   */
  addDerivedEdge(derivedEdge: MMEdge) {
    this.derivedEdges.push(derivedEdge);
  }

  /**
   * Clear the .derivedEdge array. Handle deletion for each former derived edge.
   */
  removeAllDerived() {
    for (const derivedEdge of this.derivedEdges) {
      derivedEdgeArray.removeEdge(derivedEdge);
    }
    this.derivedEdges = [];
  }
}

class EdgeArray {
  edges: Array<MMEdge>;
  constructor() {
    this.edges = [];
  }

  /**
   * Add an edge to the edge array
   * @param newEdge
   */
  addEdge(newEdge: MMEdge) {
    this.edges.push(newEdge);
  }

  /**
   * Set given edge as deleted and remove it from the edge array.
   * @param edge - Edge to be removed
   */
  removeEdge(edge: MMEdge) {
    // 跳过 undefined/null 的边
    if (!edge) return;
    edge.deleted = true;
    var index = this.edges.indexOf(edge);
    if (index !== -1) {
      this.edges.splice(index, 1);
    }
  }

  /**
   * Remove given edges. Used only with original edges.
   * @param edges - Original edges
   */
  removeOriginalEdges(edges: Array<MMEdge>) {
    for (const edge of edges) {
      this.removeEdge(edge);
    }
  }

  /**
   * Find all outgoing edges for a given model node.
   * @param node - Model node
   * @returns - Array of outgoing edges
   */
  findOutgoingEdges(node: MMNode): Array<MMEdge> {
    let returnArray: Array<MMEdge> = [];
    this.edges.forEach(edge => {
      if (edge.source == node) returnArray.push(edge);
    });
    return returnArray;
  }

  /**
   * Find all ingoing edges for a given model node.
   * @param node - Model node
   * @returns - Array of ingoing edges
   */
  findIngoingEdges(node: MMNode): Array<MMEdge> {
    let returnArray: Array<MMEdge> = [];
    this.edges.forEach(edge => {
      if (edge.target == node) returnArray.push(edge);
    });
    return returnArray;
  }

  /**
   * Find all related edges (ingoing and outgoing) for a given model node.
   * @param node - Model node
   * @returns - Array of related edges
   */
  findRelatedEdges(node: MMNode): Array<MMEdge> {
    let returnArray: Array<MMEdge> = [];
    returnArray.concat(this.findIngoingEdges(node));
    returnArray.concat(this.findOutgoingEdges(node));
    return returnArray;
  }

  /**
   * Query edge array and try to find given node's structural parent. Parent in aggragation, classification,...
   * @param node - Child node
   * @returns - Parent node or null
   */
  findStructuralParent(node: MMNode): MMNode | null {
    if (node.type == 'state') return node.parent as MMNode;
    for (const edge of this.edges) {
      if (
        hierarchicalStructuralEdges.includes(edge.type) &&
        edge.target === node
      )
        return edge.source;
    }
    return null;
  }

  /**
   * Find an existing edge in master model based on given model nodes.
   * @param source
   * @param target
   * @returns - Found edge or null
   */
  findEdgeByEndpoints(source: MMNode, target: MMNode): MMEdge | null {
    const outgoingEdges = this.findOutgoingEdges(source);
    for (const edge of outgoingEdges) {
      if (edge.target === target) return edge;
    }
    return null;
  }
}

let originalEdgeArray = new EdgeArray();
let derivedEdgeArray = new EdgeArray();

//used when importing from JSON
const importEdgeArrays = (
  newEdgeArray: EdgeArray,
  newDerivedEdgeArray: EdgeArray
) => {
  originalEdgeArray = newEdgeArray;
  derivedEdgeArray = newDerivedEdgeArray;
};

// 重置为空的边数组（新建本体图时调用）
const resetEdgeArrays = () => {
  originalEdgeArray = new EdgeArray();
  derivedEdgeArray = new EdgeArray();
};

export {
  originalEdgeArray,
  derivedEdgeArray,
  MMEdge,
  EdgeArray,
  importEdgeArrays,
  resetEdgeArrays,
};
